/**
 * JUB-51: per-guest trackable short links on the Guests tab.
 *
 * The owner creates a short link (/e/{code}) for an invited guest, sees the
 * full URL with its visit count, copies it, and deletes it after confirming.
 * Links are loaded once per event by useEventShortLinks and grouped by the
 * guest they were created for (the RSVP's primary guest UUID).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Link2, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "@/hooks/useTranslation";
import type { ShortLink } from "@/services/api/shortLinks";
import { shortLinkUrl, type EventShortLinksState } from "@/hooks/useEventShortLinks";

interface GuestShortLinksProps {
  guestUuid: string;
  guestName: string;
  state: EventShortLinksState;
}

export function GuestShortLinks({ guestUuid, guestName, state }: GuestShortLinksProps) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ShortLink | null>(null);
  const [deleting, setDeleting] = useState(false);
  // No state updates once the row is gone (collapsed, filtered, tab switched).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const guestLinks = useMemo(
    () => state.links.filter((link) => link.guestUuid === guestUuid),
    [state.links, guestUuid],
  );

  const handleCreate = async () => {
    setCreating(true);
    await state.create(guestUuid);
    if (mountedRef.current) setCreating(false);
  };

  const handleCopy = async (link: ShortLink) => {
    try {
      await navigator.clipboard.writeText(shortLinkUrl(link));
      toast.success(t("shortLinks.copied"));
    } catch {
      toast.error(t("shortLinks.copyFailed"));
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const ok = await state.remove(pendingDelete);
    if (!mountedRef.current) return;
    setDeleting(false);
    if (ok) setPendingDelete(null);
  };

  const headingId = `short-links-heading-${guestUuid}`;

  return (
    <section
      data-testid="guest-short-links"
      aria-labelledby={headingId}
      className="p-4 pl-4 sm:pl-12 border-b space-y-3"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h4 id={headingId} className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Link2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {t("shortLinks.title")}
          </h4>
          <p className="text-xs text-muted-foreground">{t("shortLinks.description")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="short-link-create"
          onClick={handleCreate}
          disabled={creating || state.loading || state.loadFailed}
          aria-label={t("shortLinks.createFor", { name: guestName })}
          className="w-full sm:w-auto shrink-0"
        >
          {creating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          {t("shortLinks.create")}
        </Button>
      </div>

      {state.loadFailed && (
        <div
          role="alert"
          data-testid="short-links-load-failed"
          className="flex flex-wrap items-center gap-2 text-xs text-destructive"
        >
          <span>{t("shortLinks.loadFailed")}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="short-links-retry"
            onClick={state.retry}
            disabled={state.loading}
          >
            {t("shortLinks.retry")}
          </Button>
        </div>
      )}

      {!state.loadFailed && guestLinks.length > 0 && (
        <ul className="space-y-2" aria-label={t("shortLinks.listLabel", { name: guestName })}>
          {guestLinks.map((link) => {
            const url = shortLinkUrl(link);
            return (
              <li
                key={link.uuid}
                data-testid="short-link-item"
                className="flex flex-col gap-2 rounded-md border bg-background p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="short-link-url"
                    className="block truncate font-mono text-sm text-primary underline-offset-2 hover:underline"
                  >
                    {url}
                  </a>
                  <p data-testid="short-link-visits" className="text-xs text-muted-foreground">
                    {t("shortLinks.visits", { count: link.visits ?? 0 })}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="short-link-copy"
                    onClick={() => handleCopy(link)}
                    aria-label={t("shortLinks.copyLabel", { url })}
                  >
                    <Copy className="mr-1 h-4 w-4" aria-hidden="true" />
                    {t("shortLinks.copy")}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="short-link-delete"
                    onClick={() => setPendingDelete(link)}
                    aria-label={t("shortLinks.deleteLabel", { url })}
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" />
                    {t("shortLinks.delete")}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && !deleting && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("shortLinks.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("shortLinks.deleteConfirmDescription", { url: pendingDelete ? shortLinkUrl(pendingDelete) : "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="short-link-delete-confirm"
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("shortLinks.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
